






































































import Vue, { PropType } from "vue";

import {
  DynamicDocumentData,
  InstallationModule,
  DynamicDocumentGroup,
  DynamicDocumentItem,
  Nullable,
  DynamicDocumentItemData,
  InstallationModuleData,
  MechanicInstallation
} from "@/types";

import ExpandableItems from "./ExpandableItems.vue";
import DocumentItem from "./DocumentItem.vue";
import documentCtxMixin from "./document-ctx";
import { imageUrlsForItem } from "@/utilities";
import { sectionAnchorId } from "@/utilities/dynamic-document";

type ItemGroup = {
  items: DynamicDocumentItem[];
  expandable?: boolean;
};

export default documentCtxMixin.extend({
  components: {
    ExpandableItems,
    DocumentItem
  },

  props: {
    group: {
      type: Object as PropType<DynamicDocumentGroup>
    },

    data: {
      type: Array as PropType<DynamicDocumentData>,
      default: () => []
    },

    /** See Document.vue's prop of the same name. */
    showStatus: {
      type: Boolean,
      default: false
    },

    moduleData: {
      type: Object as PropType<InstallationModuleData>
    },

    module: {
      type: Object as PropType<InstallationModule>
    },

    installation: {
      type: Object as PropType<MechanicInstallation>
    },

    activePanels: {
      type: Array as PropType<number[]>,
      default: () => []
    },

    panelIndex: {
      type: Number,
      default: undefined
    },

    transition: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isSavingModule: false
    };
  },

  computed: {
    anchorId(): string {
      return sectionAnchorId(this.group.title);
    },

    // The `data` prop under a name the expandable-items slot scope does not shadow — inside that
    // slot `data` is one item's row, and a renderer resolving a cross-field `required` needs all
    // of them.
    documentData(): DynamicDocumentItemData[] {
      return this.data;
    },

    /**
     * Every question in this section that the monteur still has to answer is answered.
     *
     * Reads the same `isValid` the per-item markers do, which is already "not required, hidden, or
     * filled in" — so a section with nothing left open goes green even when optional questions in
     * it are still empty.
     */
    groupComplete(): boolean {
      return this.group.items.every(item => this.itemData(item)?.isValid !== false);
    },

    itemData() {
      return (item: DynamicDocumentItem | string): DynamicDocumentItemData =>
        this.data.find(data => {
          if (typeof item === "string") return data.field === item;

          return data.field === item.field;
        })!;
    },

    isActive(): boolean {
      return this.activePanels.includes(this.panelIndex);
    },

    // Roll-up: surface the same missing-on-server warning on the collapsed
    // section header when any of its photo questions is affected, so the
    // mechanic sees it without expanding every section.
    groupMissingOnServer(): boolean {
      return this.group.items.some(item => {
        const data = this.data.find(d => d.field === item.field);

        return imageUrlsForItem(item, data).some(url =>
          this.$store.getters["library/missingOnServer"](url)
        );
      });
    },

    groupedItems(): ItemGroup[] {
      let groupExpandable: Nullable<boolean> = null;

      return this.group.items.reduce((groups, item) => {
        let group = groups[groups.length - 1];
        let itemExpandable = this.isExpandable(item);

        if (itemExpandable !== groupExpandable) {
          group = { items: [item], expandable: itemExpandable };
          groups.push(group);
        } else {
          group.items.push(item);
        }

        groupExpandable = itemExpandable;

        return groups;
      }, [] as ItemGroup[]);
    },

    isExpandable() {
      return (item: DynamicDocumentItem) => {
        switch (item.type) {
          case "IMAGE":
          case "SIGNATURE":
          case "TEXT":
          case "HIGH-LOW":
            return true;
          case "CUSTOM":
            return item.field === "layoutPlan";
          default:
            return false;
        }
      };
    }
  },

  methods: {
    async commitDocument() {
      try {
        if (this.ctx.commitDocument) {
          await this.ctx.commitDocument(this.data);
        } else {
          await this.$store.dispatch("deliveryDocuments/update", {
            data: this.data,
            moduleType: this.module.type,
            id: this.installation.id
          });
        }

        this.$emit("save:document");
      } catch (e) {
        //
      }
    },

    async commitChanges() {
      if (this.isSavingModule) {
        return;
      }

      this.isSavingModule = true;

      try {
        await this.$store.dispatch("installation/update", {
          installation: this.installation,
          moduleType: this.module.type,
          data: this.moduleData
        });

        this.$emit("save:module");
      } catch (e) {
        //
      } finally {
        this.isSavingModule = false;
      }
    }
  }
});
